/**
 * Error handling utilities for API responses
 */

import notificationService from './notificationService.js'

/**
 * Parses validation errors from API response
 * @param {Object} errorResponse - The error response object from API
 * @returns {string} - Formatted error message
 */
export function parseValidationErrors(errorResponse) {
  if (!errorResponse || !errorResponse.errors) {
    return 'An error occurred. Please try again.'
  }

  const errors = errorResponse.errors
  const errorMessages = []

  // Handle different error formats
  if (typeof errors === 'object') {
    Object.keys(errors).forEach(field => {
      const fieldErrors = errors[field]
      if (Array.isArray(fieldErrors)) {
        fieldErrors.forEach(error => {
          errorMessages.push(`${field}: ${error}`)
        })
      } else if (typeof fieldErrors === 'string') {
        errorMessages.push(`${field}: ${fieldErrors}`)
      }
    })
  }

  // If no specific field errors, check for general message
  if (errorMessages.length === 0 && errorResponse.title) {
    errorMessages.push(errorResponse.title)
  }

  // If still no messages, use a default
  if (errorMessages.length === 0) {
    errorMessages.push('Validation failed. Please check your input.')
  }

  return errorMessages.join('\n')
}

/**
 * Shows error alert to user using notification service
 * @param {string} message - Error message to display
 * @param {string} type - Type of error (error, warning, info)
 */
export function showErrorAlert(message, type = 'error') {
  // Use notification service for better UX
  notificationService[type](message, 8000) // Show for 8 seconds for errors
}

/**
 * Handles different types of API errors
 * @param {Object} error - The error object from axios
 * @returns {string} - Appropriate error message
 */
export function handleApiError(error) {
  if (!error.response) {
    return 'Network error. Please check your connection.'
  }

  const { status, data } = error.response

  switch (status) {
    case 400:
      // Check if it's a validation error
      if (data && data.errors) {
        return parseValidationErrors(data)
      }
      return data?.title || 'Invalid request. Please check your input.'
    
    case 401:
      return 'Session expired or you are not authorized for this action.'
    
    case 403:
      return 'Access denied. You do not have permission to perform this action.'
    
    case 404:
      return 'Resource not found.'

    case 408:
      return 'The import took too long and the gateway closed the connection (stream timeout). Try splitting the file into smaller batches, or ask your administrator to redeploy the gateway with a longer idle timeout.'

    case 499:
      return 'The connection closed before the import finished (often due to timeout or a large file). Try splitting the file, or ask your administrator.'

    case 405:
      return 'Method not allowed.'
    
    case 422:
      return data?.title || 'Validation error occurred.'
    
    case 500:
      return 'Internal server error. Please try again later.'
    
    default:
      if (status >= 500) {
        return 'Server error. Please try again later.'
      }
      return data?.title || data?.message || 'An unexpected error occurred.'
  }
}

/**
 * Enhanced error handler that shows alerts and returns formatted message
 * @param {Object} error - The error object from axios
 * @param {boolean} showAlert - Whether to show alert to user (default: true)
 * @param {Object} component - Vue component instance to store errors (optional)
 * @returns {string} - Formatted error message
 */
export function handleError(error, showAlert = true, component = null) {
  const message = handleApiError(error)
  
  // Store validation errors in component state for persistent display
  if (component && error.response && error.response.status === 400 && error.response.data && error.response.data.errors) {
    component.validationErrors = parseValidationErrors(error.response.data)
    component.hasValidationErrors = true
  }
  
  if (showAlert) {
    showErrorAlert(message)
  }
  
  return message
}

/**
 * Clear validation errors from component state
 * @param {Object} component - Vue component instance
 */
export function clearValidationErrors(component) {
  if (component) {
    component.validationErrors = null
    component.hasValidationErrors = false
  }
}
